import client from './client'
import type { Sacco, SaccoStats, User, Hostname, SaccoCreateForm, SaccoUpdateForm, PaginationMeta } from '@/types'

interface PaginatedResponse<T> {
  data: T[]
  meta: PaginationMeta
}

export const saccosApi = {
  async list(params: { page?: number; per_page?: number; search?: string; is_active?: boolean } = {}): Promise<PaginatedResponse<Sacco>> {
    const response = await client.get<{ data: Sacco[]; meta: PaginationMeta }>('/saccos', { params })
    return { data: response.data.data, meta: response.data.meta! }
  },

  async get(id: string): Promise<Sacco> {
    const response = await client.get<{ data: Sacco }>(`/saccos/${id}`)
    return response.data.data
  },

  async create(data: SaccoCreateForm): Promise<Sacco> {
    const response = await client.post<{ data: Sacco }>('/saccos', data)
    return response.data.data
  },

  async update(id: string, data: SaccoUpdateForm): Promise<Sacco> {
    const response = await client.put<{ data: Sacco }>(`/saccos/${id}`, data)
    return response.data.data
  },

  async delete(id: string): Promise<void> {
    await client.delete(`/saccos/${id}`)
  },

  async getStats(id: string): Promise<SaccoStats> {
    const response = await client.get<{ data: SaccoStats }>(`/saccos/${id}/stats`)
    return response.data.data
  },

  async listUsers(
    id: string,
    params: {
      page?: number
      per_page?: number
      search?: string
      role?: string
      status?: string
      with_main_balance?: boolean
    } = {},
  ): Promise<PaginatedResponse<User & { main_wallet_balance?: number }>> {
    const response = await client.get<{
      data: (User & { main_wallet_balance?: number })[]
      meta: PaginationMeta
    }>(`/saccos/${id}/users`, { params })
    return { data: response.data.data, meta: response.data.meta! }
  },

  async listVehicles(
    id: string,
    params: { page?: number; per_page?: number; with_today_dva?: boolean } = {},
  ): Promise<PaginatedResponse<{
    id: string
    number: string
    registration: string
    status: string
    today_collected?: number
    today_transferred_in?: number
    today_transferred_out?: number
  }>> {
    const response = await client.get<{
      data: {
        id: string
        number: string
        registration: string
        status: string
        today_collected?: number
        today_transferred_in?: number
        today_transferred_out?: number
      }[]
      meta: PaginationMeta
    }>(`/saccos/${id}/vehicles`, { params })
    return { data: response.data.data, meta: response.data.meta! }
  },

  async addHostname(id: string, fqdn: string): Promise<Hostname> {
    const response = await client.post<{ data: Hostname }>(`/saccos/${id}/hostnames`, { fqdn })
    return response.data.data
  },

  async removeHostname(saccoId: string, hostnameId: string): Promise<void> {
    await client.delete(`/saccos/${saccoId}/hostnames/${hostnameId}`)
  },

  async uploadImage(file: File, folder: string, publicId: string): Promise<string> {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('folder', folder)
    formData.append('public_id', publicId)
    const response = await client.post<{ data: { url: string } }>('/upload/image', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return response.data.data.url
  },
}
